import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Stack, FormControl, Select, MenuItem, Paper, Container } from '@mui/material';
import TemplateGallery from '@/pages/templates/components/TemplateGallery';
import { deleteTemplate, fetchTemplates, templateListSelector } from '@/features/admin/templates/slice';
import { useConfirmation, useAppDispatch, useAppSelector } from '@/hooks';
import { Template } from '@/types';
import { useEffect, useState } from 'react';
import routes from '@/constants/routes';
import PageHeader from "@/components/PageHeader";
import TemplateDrawer from "./components/TemplateDrawer";
import { industries } from '@/constants/industries';

const Templates = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const container = useAppSelector((state) => state.ui?.container);
  const { templateId } = useParams<{ templateId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedIndustry = searchParams.get('industry') ?? 'all';
  const selectedStatus = searchParams.get('status') ?? 'all';
  const selectedPublic = searchParams.get('public') ?? 'all';

  const setSelectedIndustry = (val: string) => {
    setSearchParams((prev) => {
      if (val === 'all') {
        prev.delete('industry');
      } else {
        prev.set('industry', val);
      }
      return prev;
    }, { replace: true });
  };

  const setSelectedStatus = (val: string) => {
    setSearchParams((prev) => {
      if (val === 'all') {
        prev.delete('status');
      } else {
        prev.set('status', val);
      }
      return prev;
    }, { replace: true });
  };

  const setSelectedPublic = (val: string) => {
    setSearchParams((prev) => {
      if (val === 'all') {
        prev.delete('public');
      } else {
        prev.set('public', val);
      }
      return prev;
    }, { replace: true });
  };

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState<Template | null>(null);

  const [renderConfirmationDialog, confirmTemplateDeletion] = useConfirmation({
    title: 'Eliminar',
    message: 'Desea eliminar el menú',
    dispatcher: (id?: string) => id && dispatch(deleteTemplate(id)),
  }) as [() => React.ReactNode, (id: string) => void];

  useEffect(() => {
    dispatch(fetchTemplates());
  }, [])

  const onDelete = (templateId: Template['id']) => {
    confirmTemplateDeletion(templateId);
  };

  const onEdit = (templateId: Template['id']) => {
    navigate(
      routes.templates.edit
        .replace(':templateId', templateId)
        .replace(':organizationId', 'ACME')
    );
  };

  const onSettingsClick = (template: Template) => {
    const paramsStr = searchParams.toString();
    navigate(`/super-admin/plantillas/${template.id}${paramsStr ? `?${paramsStr}` : ''}`);
  };

  const templates = useAppSelector(templateListSelector) as Template[];

  useEffect(() => {
    if (templateId && templates.length > 0) {
      const template = templates.find((t) => t.id === templateId);
      if (template) {
        setTemplateToEdit(template);
        setIsDrawerOpen(true);
      }
    } else if (!templateId) {
      setIsDrawerOpen(false);
      setTemplateToEdit(null);
    }
  }, [templateId, templates]);

  const filteredTemplates = templates.filter((t) => {
    if (selectedIndustry !== 'all') {
      if (!t.industry || !t.industry.includes(selectedIndustry as any)) {
        return false;
      }
    }
    if (selectedStatus !== 'all') {
      const isPublished = selectedStatus === 'published';
      if (t.isPublished !== isPublished) {
        return false;
      }
    }
    if (selectedPublic !== 'all') {
      const wantPublic = selectedPublic === 'public';
      const isTemplatePrivate = !!t.isPrivate;
      if (wantPublic && isTemplatePrivate) {
        return false;
      }
      if (!wantPublic && !isTemplatePrivate) {
        return false;
      }
    }
    return true;
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Plantillas"
        action={
          <Button color="success" size="large" onClick={() => {
            setTemplateToEdit(null);
            setIsDrawerOpen(true);
          }}>
            Crear
          </Button>
        }
      />
      <Stack>
        <Container
          maxWidth={container === 'fixed' ? 'lg' : false}
          sx={{
            height: '100%',
          }}
        >
          <Stack spacing={2}>
            <Paper sx={{ padding: 2 }}>
              <Stack direction="row" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                    id="industry-filter"
                    value={selectedIndustry}
                    onChange={(e) => setSelectedIndustry(e.target.value)}
                  >
                    <MenuItem value="all">Todas las industrias</MenuItem>
                    {Object.values(industries).map((ind) => (
                      <MenuItem key={ind.id} value={ind.id}>
                        {ind.locale.es}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    id="status-filter"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  >
                    <MenuItem value="all">Todos los estatus</MenuItem>
                    <MenuItem value="published">Publicado</MenuItem>
                    <MenuItem value="unpublished">Despublicado</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    id="public-filter"
                    value={selectedPublic}
                    onChange={(e) => setSelectedPublic(e.target.value)}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="public">Si</MenuItem>
                    <MenuItem value="private">No</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Paper>


            <TemplateGallery
              type={'edit'}
              templates={filteredTemplates}
              onEdit={onEdit}
              onDelete={onDelete}
              onSettingsClick={onSettingsClick}
            />
            {renderConfirmationDialog()}
            <TemplateDrawer
              open={isDrawerOpen}
              onClose={() => {
                setIsDrawerOpen(false);
                setTemplateToEdit(null);
                if (templateId) {
                  const paramsStr = searchParams.toString();
                  navigate(`/super-admin/plantillas${paramsStr ? `?${paramsStr}` : ''}`);
                }
              }}
              templateToEdit={templateToEdit}
            />
          </Stack>
        </Container>
      </Stack>
    </Stack>
  );
};

export default Templates;
